import React, { Fragment, useEffect } from 'react';

import { IDropdownItem } from '../../Dropdown/models/IDropdownItem.js';
import { IPagingButtons } from '../models/IPagingButtons.js';
import { IPagingPageSelection } from '../models/IPagingPageSelection.js';
import { IPagingValues } from '../models/IPagingValues.js';
import PageBackwards from '../styledComponents/PageBackwards.js';
import PageFirstButton from '../styledComponents/PageFirstButton.js';
import PageForwards from '../styledComponents/PageForwards.js';
import PageLastButton from '../styledComponents/PageLastButton.js';
import StyledPageNavigation from '../styledComponents/PageNavigation.js';

import { PageButton } from './PageButton.js';
import { PageSelection } from './PageSelection.js';

interface PageNavigationProps {
	layout?: {
		pageNavigationOrder?: PageNavigationOrder[];
	};
	pageSelection?: IPagingPageSelection;
	pageOptions: IDropdownItem[];
	pagingButtons?: IPagingButtons;
	pagingValues: IPagingValues;
	handlePageBackward: () => void;
	handlePageForward: () => void;
	handleFirstPage: () => void;
	handleLastPage: () => void;
	handlePageChange: (item: IDropdownItem | IDropdownItem[], index?: number) => void;
}

type PageNavigationOrder = 'First' | 'Previous' | 'PageSelection' | 'Next' | 'Last';

const defaultPagingButtons: IPagingButtons = {
	previous: {
		iconProps: {
			asset: 'base.paging.leftArrow',
		},
		disabledIconProps: {
			asset: 'base.paging.leftArrowDisabled',
		},
	},
	next: {
		iconProps: {
			asset: 'base.paging.rightArrow',
		},
		disabledIconProps: {
			asset: 'base.paging.rightArrowDisabled',
		},
	},
	first: {
		iconProps: {
			asset: 'base.paging.pagingFirstArrow',
		},
		disabledIconProps: {
			asset: 'base.paging.pagingFirstArrowDisabled',
		},
	},
	last: {
		iconProps: {
			asset: 'base.paging.pagingLastArrow',
		},
		disabledIconProps: {
			asset: 'base.paging.pagingLastArrowDisabled',
		},
	},
};

const mergePagingButtons = (pagingButtons: IPagingButtons): IPagingButtons => {
	const mergedButtons: IPagingButtons = {
		previous: {
			...defaultPagingButtons?.previous,
			...pagingButtons?.previous,
			iconProps: {
				...defaultPagingButtons?.previous?.iconProps,
				...pagingButtons?.previous?.iconProps,
			},
			disabledIconProps: {
				...defaultPagingButtons?.previous?.disabledIconProps,
				...pagingButtons?.previous?.disabledIconProps,
			},
		},
		next: {
			...defaultPagingButtons?.next,
			...pagingButtons?.next,
			iconProps: {
				...defaultPagingButtons?.next?.iconProps,
				...pagingButtons?.next?.iconProps,
			},
			disabledIconProps: {
				...defaultPagingButtons?.next?.disabledIconProps,
				...pagingButtons?.next?.disabledIconProps,
			},
		},
		first: {
			...defaultPagingButtons?.first,
			...pagingButtons?.first,
			iconProps: {
				...defaultPagingButtons?.first?.iconProps,
				...pagingButtons?.first?.iconProps,
			},
			disabledIconProps: {
				...defaultPagingButtons?.first?.disabledIconProps,
				...pagingButtons?.first?.disabledIconProps,
			},
		},
		last: {
			...defaultPagingButtons?.last,
			...pagingButtons?.last,
			iconProps: {
				...defaultPagingButtons?.last?.iconProps,
				...pagingButtons?.last?.iconProps,
			},
			disabledIconProps: {
				...defaultPagingButtons?.last?.disabledIconProps,
				...pagingButtons?.last?.disabledIconProps,
			},
		},
	};

	return mergedButtons;
};

export const PageNavigation: React.FC<PageNavigationProps> = ({
	layout = {},
	pageOptions = [],
	pagingButtons = {},
	pagingValues: initialPagingValues,
	pageSelection = {},
	handlePageBackward,
	handlePageForward,
	handleFirstPage,
	handleLastPage,
	handlePageChange,
}) => {
	const [pagingValues, setPagingValues] = React.useState(initialPagingValues);
	const { pageNavigationOrder = ['Previous', 'PageSelection', 'Next'] } = layout;
	const mergedPagingButtons = mergePagingButtons(pagingButtons);
	useEffect(() => {
		setPagingValues(initialPagingValues);
	}, [initialPagingValues]);
	const pageNavigationOptions = {
		First: (
			<PageFirstButton>
				<PageButton
					key="first"
					disabled={pagingValues.currentPage === 1}
					pagingButton={mergedPagingButtons.first}
					onClick={handleFirstPage}
					buttonText="First"
					label="Go to First page"
				/>
			</PageFirstButton>
		),
		Previous: (
			<PageBackwards>
				<PageButton
					key="previous"
					disabled={pagingValues.currentPage === 1}
					pagingButton={mergedPagingButtons.previous}
					onClick={handlePageBackward}
					buttonText="Previous"
					label="Go to Previous page"
				/>
			</PageBackwards>
		),
		Next: (
			<PageForwards>
				<PageButton
					key="next"
					disabled={pagingValues.currentPage >= pagingValues.totalPages}
					pagingButton={mergedPagingButtons.next}
					onClick={handlePageForward}
					buttonText="Next"
					label="Go to Next page"
				/>
			</PageForwards>
		),
		Last: (
			<PageLastButton>
				<PageButton
					key="last"
					disabled={pagingValues.currentPage >= pagingValues.totalPages}
					pagingButton={mergedPagingButtons.last}
					onClick={handleLastPage}
					buttonText="Last"
					label="Go to Last page"
				/>
			</PageLastButton>
		),
		PageSelection: (
			<PageSelection
				key="pageSelection"
				currentPage={pagingValues.currentPage}
				pageOptions={pageOptions}
				placeholders={pagingValues}
				handlePageChange={handlePageChange}
				pageSelection={pageSelection}
			/>
		),
	};
	return (
		<StyledPageNavigation>
			{pageNavigationOrder.map((pageNavigationComponent) => {
				return (
					<Fragment key={pageNavigationComponent}>
						{pageNavigationOptions[pageNavigationComponent as PageNavigationOrder]}
					</Fragment>
				);
			})}
		</StyledPageNavigation>
	);
};
